// Core domain types
export interface Division {
  id: string;
  name: string;
  code?: string;
  description?: string;
  active: boolean;
  createdAt: string;
  updatedAt: string;
  settings?: DivisionSettings;
  /** Opaque, non-secret id used to build the public self-registration link (/register/{slug}). */
  publicRegistrationSlug?: string | null;
  /** When true (and publicRegistrationSlug is set), this division appears on the shared /register page. */
  includeInPublicRegistrationList?: boolean;
}

export interface DivisionSettings {
  notificationSettings: NotificationSettings;
  escalationSettings: EscalationSettings;
  approvalThresholds: ApprovalThreshold[];
  defaultRoutingRuleJsonPath?: string;
  defaultWorkflowSteps?: DefaultWorkflowStep[];
  /** Id of the WorkflowTemplate used to build workflow steps when registration auto-creates a workflow. Takes precedence over defaultWorkflowSteps. */
  registrationWorkflowTemplateId?: string;
  defaultStepTimeoutHours?: number;
  defaultPauseTimeoutHours?: number;
  defaultTimeoutWarningHours?: number;
  timeoutWarningNotificationEmail?: string;
  requesterEmailPaths?: string[];
  requesterNamePaths?: string[];
  /** Days of inactivity (no login) after which a user is automatically deactivated. Undefined disables the feature. */
  inactivityDeactivationDays?: number;
  /** Days before auto-deactivation to send warning emails, e.g. [14, 7, 1]. Undefined/empty disables warnings. */
  inactivityWarningDaysList?: number[];
  /** When true, enforces draft/Submit Review at every step of every workflow in this division, regardless of the workflow's or step's own setting. */
  requireExplicitSubmit?: boolean;
  /** Email domains allowed to self-register, e.g. "army.mil" (specific domain) or "mil" (any subdomain of that top-level/public suffix). Undefined/empty allows any domain. */
  allowedRegistrationEmailDomains?: string[];
}

export interface DefaultWorkflowStep {
  step: number;
  name: string;
  description?: string;
  approverType: string; // 'division_role' | 'user_specific'
  divisionRoleId?: string;
  requiredApprovals: number;
  parallelApproval: boolean;
  timeoutHours?: number;
  escalationHours?: number;
}

export interface NotificationSettings {
  emailNotifications: boolean;
  slackNotifications: boolean;
  approvalReminders: boolean;
  reminderIntervalHours: number;
}

export interface EscalationSettings {
  autoEscalation: boolean;
  escalationDelayHours: number;
  maxEscalationLevels: number;
}

export interface ApprovalThreshold {
  minAmount: number;
  maxAmount?: number;
  requiredApprovals: number;
  requiredRoles: string[];
}

export interface User {
  id: string;
  username: string;
  email: string;
  firstName: string;
  lastName: string;
  isActive: boolean;
  divisionId: string;
  divisionRoleId?: string; // DEPRECATED - users can now have multiple division roles
  createdAt: string;
  updatedAt: string;
}

export interface UserRole {
  id: string;
  name: 'Shopper' | 'Approver' | 'Vendor' | 'Admin' | 'DivisionAdmin';
  description: string;
}

export interface UserDivision {
  divisionId: string;
  divisionName: string;
  roles: string[];
}

export interface Order {
  id: string;
  orderNumber: string;
  title: string;
  description: string;
  status: OrderStatus;
  totalAmount: number;
  currency: string;
  submitterId: string;
  submitterName: string;
  divisionId: string;
  divisionName: string;
  createdAt: string;
  updatedAt: string;
}

export type OrderStatus = 
  | 'Draft'
  | 'Submitted' 
  | 'Processing'
  | 'Paused'
  | 'Pending'
  | 'Completed'
  | 'PartiallyCompleted'
  | 'Cancelled'
  | 'Declined'
  | 'NeedsResolved'
  | 'Error';

// API Response types
export interface ApiResponse<T> {
  data: T;
  success: boolean;
  message?: string;
}

export interface DivisionsListResponse {
  divisions: Division[];
  totalCount: number;
}

export interface CreateDivisionRequest {
  name: string;
  code?: string;
  description?: string;
  active: boolean;
  settings?: DivisionSettings;
  includeInPublicRegistrationList?: boolean;
  // Sourced from the id_token/userinfo profile client-side (see AuthContext), since the
  // access token's given_name/family_name claims aren't guaranteed by every Okta org.
  adminFirstName?: string;
  adminLastName?: string;
}

export interface UpdateDivisionRequest {
  name: string;
  code?: string;
  description?: string;
  active: boolean;
  settings?: DivisionSettings;
  includeInPublicRegistrationList?: boolean;
}

// ---------------------------------------------------------------------------
// Division config export / import
// ---------------------------------------------------------------------------

export interface MatchingCriteriaExport {
  field?: string;
  jsonPath?: string;
  operator?: string;
  validationRules?: {
    pattern?: string;
    minLength?: number;
    maxLength?: number;
    minValue?: number;
    maxValue?: number;
    allowedValues?: string[];
  };
}

export interface ExportMatchIdentifier {
  name: string;
  description?: string;
  type: string;
  matchingCriteria: MatchingCriteriaExport;
  active: boolean;
}

export interface DivisionConfigExport {
  schemaVersion: string;
  exportedAt: string;
  sourceDivisionName: string;
  sourceDivisionCode?: string;
  description?: string;
  settings?: DivisionSettings;
  matchIdentifiers: ExportMatchIdentifier[];
  roles: string[];
}

export interface DivisionConfigImportRequest {
  configuration: DivisionConfigExport;
  importSettings: boolean;
  importRoles: boolean;
}

export interface DivisionConfigImportResult {
  success: boolean;
  settingsUpdated: boolean;
  rolesCreated: number;
  matchIdentifiersCreated: number;
  matchIdentifiersUpdated: number;
  warnings: string[];
}

export interface PaginatedResponse<T> {
  data: T[];
  pagination: {
    page: number;
    limit: number;
    total: number;
    totalPages: number;
  };
}

export interface ApiError {
  errorCode: string;
  message: string;
  details?: Record<string, any>;
}

// UI specific types
export interface Notification {
  id: string;
  type: 'success' | 'error' | 'warning' | 'info';
  title: string;
  message: string;
  isRead: boolean;
  createdAt: string;
}

// Match Identifier types
export interface MatchIdentifier {
  id: string;
  name: string;
  description?: string;
  type: string;
  matchingCriteria: MatchingCriteria;
  active: boolean;
  usageCount?: number;
  lastUsed?: string;
  createdAt: string;
  updatedAt: string;
}

export interface MatchingCriteria {
  field?: string;
  jsonPath?: string;
  operator?: string;
  validationRules?: ValidationRules;
}

export interface ValidationRules {
  required?: boolean;
  pattern?: string;
  minLength?: number;
  maxLength?: number;
  minValue?: number;
  maxValue?: number;
  allowedValues?: string[];
}

export interface CreateMatchIdentifierRequest {
  name: string;
  description?: string;
  type: string;
  matchingCriteria: MatchingCriteria;
}

export interface UpdateMatchIdentifierRequest {
  name: string;
  description?: string;
  type: string;
  matchingCriteria: MatchingCriteria;
}

export interface MatchIdentifierTypeInfo {
  type: string;
  name: string;
  description?: string;
  operators?: MatchIdentifierOperator[];
}

export interface MatchIdentifierOperator {
  operator: string;
  name: string;
  description?: string;
  supportsValue?: boolean;
  supportsPattern?: boolean;
}

export interface TestMatchIdentifierRequest {
  name: string;
  description?: string;
  type: string;
  matchingCriteria: MatchingCriteria;
  testData: object[];
}

export interface TestMatchIdentifierResult {
  identifierId: string;
  testResults: MatchTestResult[];
  summary: {
    totalTested: number;
    matched: number;
    notMatched: number;
    matchRate: number;
  };
}

export interface MatchTestResult {
  value: any;
  extractedFrom?: string;
  matched: boolean;
  reason: string;
}

export interface MatchIdentifierTypesResponse {
  types: MatchIdentifierTypeInfo[];
}

export interface MatchIdentifiersListResponse {
  matchIdentifiers: MatchIdentifier[];
  totalCount: number;
}

// User Match Identifier types for user-specific match identifier value assignments
export interface UserMatchIdentifier {
  id: string;
  userId: string;
  matchIdentifierId: string;
  matchIdentifierName: string;
  jsonPath?: string;
  value?: string; // Made optional - users can now be assigned to match identifiers without specific values
  autoMatch: boolean;
  createdAt: string;
  updatedAt: string;
}

export interface CreateUserMatchIdentifierRequest {
  matchIdentifierId: string; // Updated to match API schema
  value?: string; // Made optional - match identifiers now only require matchIdentifierId
  autoMatch?: boolean;
}

export interface UpdateUserMatchIdentifierRequest {
  value?: string; // Made optional - match identifiers now only require value updates
  autoMatch?: boolean;
}

export interface UserMatchIdentifierListResponse {
  userId: string;
  matchIdentifiers: UserMatchIdentifier[];
  activeCount: number;
}

export interface BatchMatchIdentifierRequest {
  matchIdentifiers: CreateUserMatchIdentifierRequest[];
}

export interface BatchMatchIdentifierResponse {
  userId: string;
  successfulAssignments: number;
  errorCount: number;
  errors: string[];
}

// Organization Settings types
export interface OrganizationSettings {
  security: SecuritySettings;
  authentication: AuthenticationSettings;
  integrations: IntegrationsSettings;
}

export interface SecuritySettings {
  sessionTimeout: number;
  requireMFA: boolean;
  passwordPolicy: PasswordPolicy;
}

export interface PasswordPolicy {
  minLength: number;
  requireUppercase: boolean;
  requireLowercase: boolean;
  requireNumbers: boolean;
  requireSpecialChars: boolean;
}

export interface AuthenticationSettings {
  oidcProvider: OidcProviderSettings;
}

export interface OidcProviderSettings {
  enabled: boolean;
  providerName: string;
  providerType: string;
  configuration: OidcConfiguration;
  userMapping: UserMapping;
  roleMapping: RoleMapping;
  settings: OidcSettings;
}

export interface OidcConfiguration {
  authority: string;
  clientId: string;
  clientSecret?: string;
  redirectUri: string;
  postLogoutRedirectUri: string;
  scopes: string[];
  responseType: string;
  responseMode: string;
}

export interface UserMapping {
  emailClaim: string;
  firstNameClaim: string;
  lastNameClaim: string;
  roleClaim: string;
  divisionClaim: string;
  employeeIdClaim: string;
}

export interface RoleMapping {
  Shopper: string[];
  Approver: string[];
  DivisionAdmin: string[];
  Admin: string[];
}

export interface OidcSettings {
  autoProvisionUsers: boolean;
  autoAssignDivisions: boolean;
  requireEmailVerification: boolean;
  sessionTimeout: number;
  refreshTokenRotation: boolean;
  logoutOnTokenExpiry: boolean;
}

export interface IntegrationsSettings {
  slackWebhook?: string;
  emailProvider: string;
  smtpSettings: SmtpSettings;
}

export interface SmtpSettings {
  server: string;
  port: number;
  useTLS: boolean;
  useStartTLS: boolean;  // Explicitly support StartTLS mode (for ports like 587)
  username?: string;
  password?: string;
  fromEmail?: string;     // Sender email address (e.g., noreply@company.com)
  fromName?: string;      // Sender display name (e.g., "Company Notifications")
}

export interface UpdateOrganizationSettingsRequest {
  security?: Partial<SecuritySettings>;
  authentication?: Partial<AuthenticationSettings>;
  integrations?: Partial<IntegrationsSettings>;
}

export interface OrganizationSettingsExport {
  schemaVersion: string;
  exportedAt: string;
  organizationName: string;
  security: SecuritySettings;
  authentication: AuthenticationSettings;
  integrations: IntegrationsSettings;
}

export interface OrganizationSettingsImportRequest {
  settings: OrganizationSettingsExport;
  importSecurity: boolean;
  importAuthentication: boolean;
  importIntegrations: boolean;
}

export interface OrganizationSettingsImportResult {
  success: boolean;
  securityUpdated: boolean;
  authenticationUpdated: boolean;
  integrationsUpdated: boolean;
  warnings: string[];
}

// Division-level Workflow types based on Swagger API
export type WorkflowMatchingMode = 'none' | 'matchIdentifiers' | 'routingRules';

export interface WorkflowSummaryDto {
  id: string;
  name: string;
  description: string;
  type: string;
  active: boolean;
  matchingMode: WorkflowMatchingMode;
  matchCriteria: WorkflowMatchCriteriaDto;
  steps: WorkflowStepSummaryDto[];
  settings: WorkflowSettingsDto;
  routingRules: RoutingRuleDto[];
  createdAt: string;
  updatedAt: string;
}

export interface WorkflowDetailResponse {
  id: string;
  name: string;
  description: string;
  type: string;
  active: boolean;
  matchingMode: WorkflowMatchingMode;
  matchCriteria: WorkflowMatchCriteriaDto;
  steps: WorkflowStepDetailDto[];
  settings: WorkflowSettingsDto;
  routingRules: RoutingRuleDto[];
  usageStats: WorkflowUsageStatsDto;
  isDeleted?: boolean;
  deletedAt?: string;
  createdAt: string;
  updatedAt: string;
}

export interface WorkflowMatchCriteriaDto {
  matchIdentifiers: string[];
  executionIdentifiers: string[];
}

export interface WorkflowSettingsDto {
  autoEscalation: boolean;
  escalationHours: number;
  defaultStepTimeoutHours?: number; // Workflow-specific step timeout override (overrides division default)
  pauseTimeoutHours?: number; // Workflow-specific pause timeout override (overrides division default)
  defaultTimeoutWarningHours?: number; // Workflow-level default warning hours before timeout (overrides division default)
  notifications: WorkflowNotificationSettingsDto;
  timeoutAction: string;
  requireExplicitSubmit?: boolean; // When true, enforces draft/Submit Review at every step in this workflow regardless of the step's own setting
}

export interface WorkflowNotificationSettingsDto {
  email: boolean;
  slack: boolean;
  sms: boolean;
}

export interface RoutingRuleDto {
  id: string;
  field: string;
  operator: string;
  value: string;
  valueEnd?: string;
}

export interface WorkflowApproverReportRowDto {
  routingRuleValue: string;
  workflowId: string;
  workflowName: string;
  /** Column name (step name, e.g. "Approver 1") -> semicolon-joined approver email(s). */
  approvers: Record<string, string>;
}

export interface WorkflowApproverReportDto {
  jsonPath: string;
  generatedAt: string;
  columns: string[];
  rows: WorkflowApproverReportRowDto[];
}

export interface UserWorkflowStepAssociationDto {
  step: number;
  name: string;
  stepType: string;
  /** Why the user matched this step, e.g. "division_role", "user_specific", "match_identifier: ...". */
  matchedBy: string;
}

export interface UserWorkflowAssociationDto {
  workflowId: string;
  workflowName: string;
  workflowType: string;
  active: boolean;
  steps: UserWorkflowStepAssociationDto[];
}

export interface WorkflowUsageStatsDto {
  totalExecutions: number;
  averageCompletionTime: string;
  successRate: number;
  lastExecuted: string;
}

export interface WorkflowStepSummaryDto {
  id?: string; // Optional for backward compatibility, present when fetched from API
  step: number; // Changed from 'level' to match API property name 'Step'
  name: string;
  description: string;
  stepType: string; // "approval", "fulfillment", "notification", etc.
  canEditQuantity: boolean;
  allowQuantityIncrease?: boolean; // When false (and canEditQuantity is true), approvers may only decrease quantities, never increase them
  orderLevelOnly: boolean; // When true, approvers can only approve/reject the entire order, not individual line items
  requireSignature?: boolean; // When true (Confirm Pickup steps), approver must draw a signature before confirming
  requireExplicitSubmit?: boolean; // When true, line decisions at this step are staged as drafts until the reviewer clicks Submit Review
  approvers: WorkflowStepApproversDto;
  conditions: WorkflowStepConditionsDto;
}

export interface WorkflowStepDetailDto {
  id: string; // Always present in detailed view
  step: number; // Changed from 'level' to match API property name 'Step'
  name: string;
  description: string;
  stepType: string; // "approval", "fulfillment", "notification", etc.
  canEditQuantity: boolean;
  allowQuantityIncrease?: boolean; // When false (and canEditQuantity is true), approvers may only decrease quantities, never increase them
  orderLevelOnly: boolean; // When true, approvers can only approve/reject the entire order, not individual line items
  requireSignature?: boolean; // When true (Confirm Pickup steps), approver must draw a signature before confirming
  requireExplicitSubmit?: boolean; // When true, line decisions at this step are staged as drafts until the reviewer clicks Submit Review
  approvers: WorkflowStepApproversDto;
  conditions: WorkflowStepConditionsDto;
}

export interface WorkflowStepApproversDto {
  type: string; // "match_identifier_based", "division_role", "role_based" (deprecated), "user_specific"
  
  // New match identifier-based configuration
  matchIdentifiers?: string[]; // Array of match identifier IDs
  
  // Division role-based configuration (replaces legacy role configuration)
  divisionRoleIds?: string[]; // Array of division role IDs
  
  // Legacy configuration (deprecated but maintained for backward compatibility)
  roles?: string[]; // DEPRECATED - use divisionRoleIds instead
  userIds?: string[]; // User-specific approvers (fully supported)
  
  // Email invitations for new users (will be created in Okta and database)
  inviteEmails?: string[]; // Email addresses to invite as approvers
  inviteDivisionRoleId?: string; // Division role assigned to users invited via inviteEmails above
  
  parallelApproval: boolean;
  requiredApprovals: number;
  timeoutHours?: number;
  timeoutWarningHours?: number; // Hours before timeout to send a warning notification to approvers
}

export interface WorkflowStepConditionsDto {
  amountThreshold?: number;
  skipIfApprovedBy: string[];
  bypassIfNoApprover?: boolean;
  auditBypass?: boolean;
}

export interface CreateWorkflowStepDto {
  step: number; // Changed from 'level' to 'step' to match API and be consistent with WorkflowStepSummaryDto
  stepType: string; // "approval", "fulfillment", "notification", etc.
  name: string;
  description: string;
  canEditQuantity: boolean;
  allowQuantityIncrease?: boolean; // When false (and canEditQuantity is true), approvers may only decrease quantities, never increase them
  orderLevelOnly: boolean; // When true, approvers can only approve/reject the entire order, not individual line items
  requireSignature?: boolean; // When true (Confirm Pickup steps), approver must draw a signature before confirming
  requireExplicitSubmit?: boolean; // When true, line decisions at this step are staged as drafts until the reviewer clicks Submit Review
  approvers: WorkflowStepApproversDto;
  conditions: WorkflowStepConditionsDto;
}

export interface CreateWorkflowRequest {
  name: string;
  description: string;
  type: string;
  matchingMode: WorkflowMatchingMode;
  matchCriteria: WorkflowMatchCriteriaDto;
  workflowSteps: CreateWorkflowStepDto[];
  routingRules: RoutingRuleDto[];
  settings: WorkflowSettingsDto;
  /** Id of the WorkflowTemplate applied in the wizard when creating this workflow, if any. */
  templateId?: string;
}

export interface UpdateWorkflowRequest {
  name?: string;
  description?: string;
  active?: boolean;
  matchingMode?: WorkflowMatchingMode;
  matchCriteria?: WorkflowMatchCriteriaDto;
  workflowSteps?: CreateWorkflowStepDto[];
  routingRules?: RoutingRuleDto[];
  settings?: WorkflowSettingsDto;
}

export interface WorkflowListResponse {
  workflows: WorkflowSummaryDto[];
  totalCount: number;
}

// Workflow Execution types
export interface ExecuteWorkflowRequest {
  orderId: string;
  externalOrderId: string;
  orderData: ExecuteWorkflowOrderDataDto;
}

export interface ExecuteWorkflowOrderDataDto {
  externalShopperId: string;
  totalAmount: number;
  priority: string;
  metadata: Record<string, any>;
}

export interface WorkflowExecutionResponse {
  executionId: string;
  workflowId: string;
  orderId: string;
  status: string;
  currentLevel: number;
  totalLevels: number;
  matchedApprovers: MatchedApproverLevelDto[];
  estimatedCompletion: string;
  startedAt: string;
}

export interface MatchedApproverLevelDto {
  // This would be defined based on the actual schema
  [key: string]: any;
}

export interface WorkflowExecutionSummaryDto {
  executionId: string;
  orderId: string;
  status: string;
  currentLevel: number;
  totalLevels: number;
  startedAt: string;
  estimatedCompletion: string;
  completedAt: string;
  completionRate: number;
}

export interface WorkflowExecutionDetailResponse {
  executionId: string;
  workflowId: string;
  workflowName: string;
  orderId: string;
  orderData: ExecuteWorkflowOrderDataDto;
  status: string;
  currentLevel: number;
  totalLevels: number;
  workflowSteps: ExecutionWorkflowStepDto[];
  startedAt: string;
  completedAt: string;
  estimatedCompletion: string;
}

export interface ExecutionWorkflowStepDto {
  // This would be defined based on the actual schema
  [key: string]: any;
}

export interface WorkflowExecutionListResponse {
  executions: WorkflowExecutionSummaryDto[];
  totalCount: number;
  pagination: PaginationDto;
}

export interface PaginationDto {
  page: number;
  limit: number;
  totalPages: number;
  hasNext: boolean;
  hasPrevious: boolean;
}

// Workflow MatchIdentifier relationship types (NEW ARCHITECTURE)
export interface MatchIdentifierSummaryDto {
  id: string;
  name: string;
  description?: string;
  type: string;
}

export interface WorkflowMatchIdentifierDto {
  id: string;
  workflowId: string;
  matchIdentifierId: string;
  isRequired: boolean;
  weight: number;
  priority: number;
  active: boolean;
  conditions?: string;
  matchIdentifier: MatchIdentifierSummaryDto;
  createdAt: string;
  updatedAt: string;
}

export interface CreateWorkflowMatchIdentifierDto {
  matchIdentifierId: string;
  isRequired: boolean;
  weight: number;
  priority: number;
  active: boolean;
  conditions?: string;
}

export interface UpdateWorkflowMatchIdentifierDto {
  isRequired?: boolean;
  weight?: number;
  priority?: number;
  active?: boolean;
  conditions?: string;
}

export interface BulkAssignWorkflowMatchIdentifiersDto {
  matchIdentifiers: CreateWorkflowMatchIdentifierDto[];
  replaceExisting: boolean;
}

export interface WorkflowMatchIdentifierListDto {
  items: WorkflowMatchIdentifierDto[];
  totalCount: number;
  page: number;
  pageSize: number;
}

// Division Users API types
export interface CreateUserRequest {
  email: string;
  firstName: string;
  lastName: string;
  address?: string;
  city?: string;
  state?: string;
  zipCode?: string;
  externalUserId?: string;
  externalProvider?: string;
  divisionRoles: CreateUserRoleRequest[]; // Array of division roles - users can have multiple roles
  createInOkta?: boolean; // Also create (or add to the configured group) this user in Okta
  systemRoles?: string[]; // Global roles (see SYSTEM_ROLE_NAMES) - omit to leave unchanged
}

export interface CreateUserRoleRequest {
  divisionId: string;
  role: string;
}

export interface UpdateUserRequest {
  email?: string;
  firstName?: string;
  lastName?: string;
  address?: string;
  city?: string;
  state?: string;
  zipCode?: string;
  active?: boolean;
  externalUserId?: string;
  externalProvider?: string;
  divisionRoles?: CreateUserRoleRequest[]; // Array of division roles - users can have multiple roles
  systemRoles?: string[]; // Global roles (see SYSTEM_ROLE_NAMES) - omit to leave unchanged
}

export interface UserDto {
  id: string;
  email: string;
  firstName: string;
  lastName: string;
  address?: string;
  city?: string;
  state?: string;
  zipCode?: string;
  active: boolean;
  externalUserId?: string;
  externalProvider?: string;
  createdAt: string;
  updatedAt: string;
  lastLoginAt?: string;
  deactivatedAt?: string;
  deactivationReason?: string;
  divisionRoles: UserRoleDto[];
  // Global (not division-scoped) roles: approvn-admin, approvn-approver, approvn-customer-support, approvn-picker.
  systemRoles: string[];
  matchIdentifierSummary?: UserMatchIdentifierSummary;
}

/** Known system role names, previously sourced from Okta groups of the same names. */
export const SYSTEM_ROLE_NAMES = {
  Admin: 'approvn-admin',
  Approver: 'approvn-approver',
  CustomerSupport: 'approvn-customer-support',
  Picker: 'approvn-picker',
} as const;

export const ALL_SYSTEM_ROLES: string[] = Object.values(SYSTEM_ROLE_NAMES);

export interface UserMatchIdentifierSummary {
  totalCount: number;
  matchIdentifiers: UserMatchIdentifierInfo[];
}

export interface UserMatchIdentifierInfo {
  matchIdentifierId: string;
  matchIdentifierName: string;
  value?: string;
}

export interface UserRoleDto {
  divisionId: string;
  divisionName: string;
  divisionCode?: string;
  role: string;
  isDivisionAdmin: boolean;
  isWorkflowAdmin: boolean;
}

export interface UserListResponse {
  users: UserDto[];
  totalCount: number;
  limit: number;
  offset: number;
}

export interface UserActivityDto {
  userId: string;
  totalOrders: number;
  pendingApprovals: number;
  completedApprovals: number;
  lastLoginAt?: string;
  lastActivityAt?: string;
  recentActivity: ActivityStatDto[];
}

export interface ActivityStatDto {
  date: string;
  activityType: string;
  count: number;
}

export interface BatchCreateUsersRequest {
  users: CreateUserRequest[];
}

export interface BatchCreateUsersResponse {
  results: BatchUserResult[];
}

export interface BatchUserResult {
  email: string;
  success: boolean;
  errorMessage?: string;
  user?: UserDto;
}

// Division User Import Types
export interface DivisionUserImportRequest {
  format: 'csv' | 'json';
  updateExisting: boolean;
  includeMatchIdentifiers: boolean;
}

export interface DivisionUserImportResult {
  divisionId: string;
  summary: UserImportSummary;
  errors: DivisionUserImportError[];
  successfulImports: DivisionUserImportSuccess[];
}

export interface DivisionUserImportError {
  rowNumber: number;
  email: string;
  errorMessage: string;
  errorType: string; // "USER_EXISTS", "INVALID_ROLE", "INVALID_MATCH_IDENTIFIER", etc.
}

export interface DivisionUserImportSuccess {
  email: string;
  userId: string;
  status: string; // "CREATED", "UPDATED"
  matchIdentifiersAssigned: number;
}

export interface UserImportSummary {
  totalRecords: number;
  successfulImports: number;
  updatedUsers: number;
  skippedRecords: number;
  errorCount: number;
}

export interface ImportUserMatchIdentifier {
  matchIdentifierId: string;
  value?: string;
  autoMatch: boolean;
}

// User Match Identifier types for Division Users API
export interface UserMatchTestResult {
  value: any;
  extractedFrom?: string;
  matched: boolean;
  reason: string;
}

export interface TestUserMatchIdentifierRequest {
  testData: object[];
}

export interface UserMatchTestResult {
  value: any;
  extractedFrom?: string;
  matched: boolean;
  reason: string;
}

export interface TestUserMatchIdentifierResponse {
  userId: string;
  testResults: UserMatchTestResult[];
  summary: {
    totalTested: number;
    matched: number;
    notMatched: number;
    matchRate: number;
  };
}

export interface AppState {
  currentUser: User | null;
  currentDivision: Division | null;
  availableDivisions: Division[];
  notifications: Notification[];
  theme: 'light' | 'dark';
  sidebarOpen: boolean;
  loading: boolean;
}

// Division Role Management Types
export interface DivisionRole {
  id: string;
  name: string;
  divisionId: string;
  includeInRegistration: boolean;
  isDivisionAdmin: boolean;
  isWorkflowAdmin: boolean;
  createdAt: string;
  updatedAt: string;
}

export interface DivisionRoleListResponse {
  roles: DivisionRole[];
}

export interface CreateDivisionRoleRequest {
  name: string;
  includeInRegistration?: boolean;
  isDivisionAdmin?: boolean;
  isWorkflowAdmin?: boolean;
}

export interface UpdateDivisionRoleRequest {
  name: string;
  includeInRegistration?: boolean;
  isDivisionAdmin?: boolean;
  isWorkflowAdmin?: boolean;
}

// API Key Management Types
export interface ApiKey {
  id: string;
  name: string;
  description?: string;
  active: boolean;
  expiresAt?: string;
  createdAt: string;
  lastUsedAt?: string;
}

export interface ApiKeyWithPlainText extends ApiKey {
  plainTextKey: string; // Only available on creation
}

export interface ApiKeyListResponse {
  apiKeys: ApiKey[];
}

export interface CreateApiKeyRequest {
  name: string;
  description?: string;
  expiresAt?: string;
}

// Order Management Types
export interface OrderSummaryDto {
  id: string;
  orderNumber: string;
  title: string;
  status: string;
  totalAmount: number;
  originalTotalAmount?: number; // Original total before modifications
  currency: string;
  submitterName: string;
  submitterEmail?: string;
  divisionId: string;
  divisionName: string;
  workflowId?: string;
  workflowName?: string;
  createdAt: string;
  updatedAt: string;
  currentStep: number;
  totalSteps: number;
  pendingApprovers: string[];
  requiredStep?: number;
  pendingSince?: string;
  timeoutAt?: string;
}

export interface ShippingAddressDto {
  street1?: string;
  street2?: string;
  city?: string;
  state?: string;
  zipCode?: string;
  country?: string;
  contactName?: string;
  phone?: string;
  email?: string;
}

export interface BillingAddressDto {
  street1?: string;
  street2?: string;
  city?: string;
  state?: string;
  zipCode?: string;
  country?: string;
  contactName?: string;
  phone?: string;
  email?: string;
}

export interface OrderDetailDto {
  id: string;
  orderNumber: string;
  title: string;
  description: string;
  notes?: string;
  deliveryNotes?: string;
  status: string;
  totalAmount: number;
  currency: string;
  submitterName: string;
  submitterEmail?: string;
  divisionId: string;
  divisionName: string;
  createdAt: string;
  updatedAt: string;
  workflowExecutionId?: string;
  workflowId?: string; // Added for editable fields routing
  pauseReason?: string; // Reason why order was paused
  rejectionReason?: string; // Reason why order was rejected/cancelled
  workflowStartFailed?: boolean; // True when Temporal failed to start the workflow (WorkflowExecution in temporal_pending state)
  temporalWorkflowId?: string; // The Temporal workflow ID (e.g. OrderApprovalWorkflow-{orderId})
  temporalWorkflowStatus?: string; // Status from WorkflowExecution: temporal_pending, temporal_running, started, in_progress, completed, failed, cancelled
  orderDocument?: any; // JsonElement from the API
  lineItems: OrderLineItemDto[];
  shippingAddress?: ShippingAddressDto;
  billingAddress?: BillingAddressDto;
  currentStep: number;
  totalSteps: number;
  stepNames?: Record<number, string>; // Map of step number to step name
  stepTypes?: Record<number, string>; // Map of step number to step type (approval, notification, fulfillment, etc.)
  currentStepCanEditQuantity: boolean; // Whether approvers at the current step can edit line item quantities
  currentStepAllowQuantityIncrease?: boolean; // Whether approvers at the current step may increase quantities (false = decrease only)
  currentStepOrderLevelOnly: boolean; // Whether the current step only allows order-level approval/rejection
  currentStepRequireSignature?: boolean; // Whether the current step (Confirm Pickup) requires a signature before confirming
  currentStepRequireExplicitSubmit?: boolean; // Whether the current step stages decisions as drafts requiring an explicit Submit Review
  approvalHistory: ApprovalHistoryDto[];
  pendingApprovers: string[];
  vendor?: VendorInfoDto;
  editableFields: OrderEditableFieldDto[]; // Included in order detail response
}

export interface OrderLineItemDto {
  id: string;
  sku: string;
  name: string;
  description?: string;
  longDescription?: string; // From Item_Item.LongDescription
  altPartNumber?: string;
  vendorPartNumber?: string; // From Order_EntryAttributes
  quantity: number;
  unitPrice: number;
  totalPrice: number;
  fullPrice?: number; // Original price before discounts
  inStockQuantity?: number;
  category?: string;
  vendor?: string;
  vendorName?: string; // From Item_Item.Item_Attributes
  manufacturerName?: string; // From Item_Item.Item_Attributes
  imageUrl?: string; // From Order_EntryAttributes
  notes?: string;
  comment?: string; // From Order_Entries.Comment
  taxable?: boolean;
  status?: number;
  unitOfMeasure?: string; // From Item_Item.UnitOfMeasure
  itemId?: string; // Reference to Item_Item.ID
  
  // Line item approval information
  approvalStatus?: string; // "pending", "approved", "rejected"
  approvalDecision?: string; // "approve", "reject", "modify"
  approvalComments?: string;
  approvalDate?: string;
  approverName?: string;
  approverEmail?: string;
  currentStep?: number; // The step this line item is currently pending at
  
  // Multi-step approval information
  stepStatuses?: Record<number, LineItemStepApprovalStatus[]>; // step -> list of approval statuses (one per approver)
  highestCompletedStep?: number;
  canProgressToNextLevel?: boolean;
  
  // Quantity modification information
  approvedQuantity?: number; // Final approved quantity (if modified)
  modificationReason?: string; // Reason for quantity modification
  isQuantityModified?: boolean; // Whether quantity was modified from original
}

export interface LineItemStepApprovalStatus {
  level: number;
  status: string; // "pending", "approved", "rejected", "not_reached"
  decision?: string; // "approve", "reject", "modify"
  comments?: string;
  approverName?: string;
  approverEmail?: string;
  decisionDate?: string;
  approvedQuantity?: number;
  modificationReason?: string;
}

export interface ApprovalHistoryDto {
  id: string;
  level: number;
  levelName?: string; // Name of the step
  approverName: string;
  approverEmail: string;
  action: string; // approve, reject, etc
  status: string;
  comments?: string;
  actionDate: string;
  responseTime?: string;
  lineItemActions?: ApprovalHistoryLineItemDto[];
  
  // Delegation information
  delegatedFromUserId?: string;
  delegatedFromUserName?: string;
  delegatedFromUserEmail?: string;
}

export interface ApprovalHistoryLineItemDto {
  lineItemId: string;
  lineItemName: string;
  decision: string; // approve, reject, modify
  comments?: string;
  originalQuantity?: number;
  approvedQuantity?: number;
  modificationReason?: string;
}

export interface VendorInfoDto {
  id: string;
  name: string;
  email?: string;
  phone?: string;
}

export interface PendingApprovalDto {
  orderId: string;
  orderNumber: string;
  title: string;
  description: string;
  status: string;
  totalAmount: number;
  originalTotalAmount?: number;
  currency: string;
  submitterName: string;
  submittedAt: string;
  step: number;
  approvalType: string;
  timeoutAt?: string;
  priority: string;
  matchedBy?: string;
}

export interface DivisionStatsDto {
  divisionId: string;
  divisionName: string;
  totalOrders: number;
  pendingApprovals: number;
  approvedOrders: number;
  rejectedOrders: number;
  totalSpend: number;
  averageOrderValue: number;
  averageApprovalTime: number;
  workflowStepStats: WorkflowStepStatsDto[];
  recentActivity: RecentActivityDto[];
}

export interface WorkflowStepStatsDto {
  level: number;
  name: string;
  totalRequests: number;
  approved: number;
  rejected: number;
  pending: number;
  averageResponseTime: number;
}

export interface RecentActivityDto {
  timestamp: string;
  activityType: string;
  description: string;
  userName: string;
  orderId?: string;
  orderNumber: string;
}

export interface PagedResult<T> {
  items: T[];
  totalCount: number;
  pageSize: number;
  page: number;
  totalPages: number;
  hasNextPage: boolean;
  hasPreviousPage: boolean;
  /** Counts by status (lowercase) across the full filtered dataset, not just the current page. */
  statusCounts?: Record<string, number>;
}

export interface DashboardSummary {
  totalOrders: number;
  actionRequiredOrders: number;
  ordersAssignedToUser: number;
  completedOrders: number;
}

// Action Management Types (formerly Approval Management)
export interface ApprovalDecisionRequest {
  userId: string;
  level: number;
  decision: 'accept' | 'decline' | 'modify';
  comments?: string;
  lineItemId?: string;
  approvedQuantity?: number;
  approvedUnitPrice?: number;
  modificationReason?: string;
  signatureData?: string; // Base64-encoded PNG data URL captured for Confirm Pickup steps requiring a signature
}

// A reviewer's locally-staged (not yet submitted) line decision for a RequireExplicitSubmit step.
export interface OrderReviewDraftDto {
  lineItemId: string;
  step: number;
  decision: 'accept' | 'decline';
  comments?: string;
  approvedQuantity?: number;
  modificationReason?: string;
  signatureData?: string;
  updatedAt: string;
}

export interface UpsertOrderReviewDraftRequest {
  step: number;
  decision: 'accept' | 'decline';
  comments?: string;
  approvedQuantity?: number;
  modificationReason?: string;
  signatureData?: string;
}

export interface LineItemApproval {
  id: string;
  orderId: string;
  lineItemId: string;
  approverId: string;
  step: number;
  decision: 'accept' | 'decline' | 'modify';
  status: 'pending' | 'accepted' | 'declined' | 'modified';
  comments?: string;
  decisionDate: string;
  includeInFulfillment: boolean;
  approvedQuantity?: number;
  approvedUnitPrice?: number;
  modificationReason?: string;
  approver?: {
    id: string;
    email: string;
    firstName?: string;
    lastName?: string;
  };
}

export interface OrderApprovalSummary {
  totalLineItems: number;
  approvedItems: number;
  rejectedItems: number;
  pendingItems: number;
  modifiedItems: number;
  approvalPercentage: number;
  rejectionPercentage: number;
  hasCriticalRejections: boolean;
  criticalRejectedItems: string[];
  canProceedWithPartialFulfillment: boolean;
  recommendedAction: 'continue_approval' | 'partial_fulfillment' | 'reject_order' | 'escalate_for_review' | 'request_modification';
}

export interface ApprovalResponse {
  message: string;
  success: boolean;
}

export interface WorkflowExecutionApproval {
  id: string;
  workflowExecutionId: string;
  userId: string;
  level: number;
  status: 'pending' | 'approved' | 'rejected';
  comments?: string;
  actionDate?: string;
  matchedBy: string;
  timeoutAt?: string;
  createdAt: string;
  updatedAt: string;
}

export interface OrderRequiringApproval extends OrderSummaryDto {
  requiredStep: number;
  pendingSince?: string;
  timeoutAt?: string;
  workflowExecutionId?: string;
  externalOrderId?: string; // This is the temporalWorkflowId
}

// Temporal Workflow Types
export interface TemporalWorkflowExecutionResponse {
  temporalWorkflowId: string;
  executionId: string;
  workflowId: string;
  orderId: string;
  status: string;
  currentLevel: number;
  totalLevels: number;
  startedAt: string;
  estimatedCompletion?: string;
  workflowSteps: TemporalWorkflowStep[];
}

export interface TemporalWorkflowStep {
  level: number;
  name: string;
  status: string;
  timeoutAt: string;
  approvers: TemporalApprover[];
}

export interface TemporalApprover {
  userId: string;
  userName: string;
  role: string;
  matchedBy: string;
  status: string;
  requestedAt?: string;
  respondedAt?: string;
  timeoutAt?: string;
  comments?: string;
  
  // Delegation information
  delegatedFromUserId?: string;
  delegatedFromUserName?: string;
  delegatedFromUserEmail?: string;
}

// Richer types returned by GET .../temporal/{id}/status
// (uses `step` to match C# Step property, includes DB-enriched approver data)
export interface TemporalWorkflowStatusDetail {
  workflowId: string;
  status: string;            // e.g. "Started", "InProgress", "Completed", "Cancelled"
  currentLevel: number;
  totalLevels: number;
  startedAt: string;
  completedAt?: string;
  result?: string;
  failureReason?: string;
  workflowSteps: TemporalWorkflowStepDetail[];
}

export interface TemporalWorkflowStepDetail {
  step: number;
  name: string;
  status: string;            // "Pending" | "InProgress" | "Completed" | "Declined" | "Timeout"
  startedAt?: string;
  completedAt?: string;
  timeoutAt?: string;
  approvers: TemporalApproverDetail[];
}

export interface TemporalApproverDetail {
  userId?: string;
  userName: string;
  role?: string;
  matchedBy?: string;
  status: string;            // "pending" | "approved" | "rejected" | "timeout"
  requestedAt?: string;
  respondedAt?: string;
  timeoutAt?: string;
  comments?: string;
  delegatedFromUserId?: string;
  delegatedFromUserName?: string;
  delegatedFromUserEmail?: string;
}

// Order Creation Types
export interface CreateOrderRequest {
  orderNumber: string;
  status: string;
  workflowExecutionId?: string;
  workflowRunId?: string;
  vendorId?: string;
  divisionId: string;
  orderDocument: any; // JSON document containing full order details
}

export interface CreateOrderFormData {
  orderNumber: string;
  vendorId: string;
  divisionId: string;
  status: 'Acknowledged' | 'PendingApproval' | 'Approved' | 'Declined' | 'PartiallyShipped' | 'Fulfilled';
  workflowExecutionId?: string;
  workflowRunId?: string;
  orderDocument: {
    title?: string;
    description?: string;
    totalAmount?: number;
    currency?: string;
    notes?: string;
    items?: OrderLineItem[];
  };
}

export interface OrderLineItem {
  productId: string;
  quantity: number;
  price: number;
  description?: string;
  notes?: string;
}

// Editable Fields Types
export interface AllowedValueOption {
  name: string;
  value: string;
}

export interface OrderAttributeDto {
  id?: string;
  workflowId: string;
  jsonPath: string;
  label: string;
  fieldType: 'string' | 'number' | 'boolean' | 'date';
  description?: string;
  isActive: boolean;
  minStep?: number;
  maxStep?: number;
  editableByRoleIds?: string[] | null;
  validationRules?: string;
  displayOrder: number;
  isRequired: boolean;
  isEditable: boolean;
  showValueInList: boolean;
  allowedValuesJsonPath?: string;
  /** Set when this attribute was inherited from a division-level template. */
  divisionTemplateId?: string;
  /** True when divisionTemplateId is set — attribute cannot be deleted from the workflow. */
  isInherited?: boolean;
  createdAt?: string;
  updatedAt?: string;
}

// Legacy alias for backward compatibility
export interface EditableFieldDto extends OrderAttributeDto {}

// ---------------------------------------------------------------------------
// Division-level Order Attribute Template types
// ---------------------------------------------------------------------------

export interface DivisionOrderAttributeTemplateDto {
  id: string;
  divisionId: string;
  jsonPath: string;
  label: string;
  fieldType: 'string' | 'number' | 'boolean' | 'date';
  description?: string;
  validationRules?: string;
  allowedValuesJsonPath?: string;
  showValueInList: boolean;
  displayOrder: number;
  isActive: boolean;
  /** Number of workflow attributes currently linked to this template. */
  workflowCount: number;
  createdAt: string;
  updatedAt?: string;
}

export interface CreateDivisionOrderAttributeTemplateRequest {
  jsonPath: string;
  label: string;
  fieldType: 'string' | 'number' | 'boolean' | 'date';
  description?: string;
  validationRules?: string;
  allowedValuesJsonPath?: string;
  showValueInList?: boolean;
  displayOrder?: number;
}

export interface UpdateDivisionOrderAttributeTemplateRequest {
  jsonPath?: string;
  label?: string;
  fieldType?: 'string' | 'number' | 'boolean' | 'date';
  description?: string;
  validationRules?: string;
  allowedValuesJsonPath?: string;
  showValueInList?: boolean;
  displayOrder?: number;
  isActive?: boolean;
}

export interface OrderAttributeFieldDto {
  editableFieldId: string;
  jsonPath: string;
  label: string;
  fieldType: string;
  description?: string;
  currentValue?: any;
  isRequired: boolean;
  isEditable: boolean;
  validationRules?: string;
  canEdit: boolean;
  lastModifiedAt?: string;
  lastModifiedBy?: string;
  allowedValues?: AllowedValueOption[];
  showValueInList?: boolean;
}

// Legacy alias for backward compatibility
export interface OrderEditableFieldDto extends OrderAttributeFieldDto {}

export interface UpdateOrderAttributeValueRequest {
  editableFieldId: string;
  newValue?: any;
  modificationReason?: string;
}

// Legacy alias for backward compatibility
export interface UpdateOrderFieldRequest extends UpdateOrderAttributeValueRequest {}

export interface UpdateOrderAttributeValueResponse {
  success: boolean;
  message?: string;
  updatedValue?: any;
  modifiedAt: string;
}

// Legacy alias for backward compatibility
export interface UpdateOrderFieldResponse extends UpdateOrderAttributeValueResponse {}

export interface FieldModificationHistoryDto {
  id: string;
  fieldLabel: string;
  jsonPath: string;
  originalValue?: string;
  newValue: string;
  modifiedBy: string;
  step: number;
  modificationReason?: string;
  modifiedAt: string;
}

export interface CreateOrderAttributeRequest {
  workflowId: string;
  jsonPath: string;
  label: string;
  fieldType: 'string' | 'number' | 'boolean' | 'date';
  description?: string;
  minStep?: number;
  maxStep?: number;
  editableByRoleIds?: string[];
  validationRules?: string;
  displayOrder: number;
  isRequired: boolean;
  isEditable?: boolean;
  showValueInList?: boolean;
  allowedValuesJsonPath?: string;
}

// Legacy alias for backward compatibility
export interface CreateEditableFieldRequest extends CreateOrderAttributeRequest {}

// ============================================
// Workflow Simulation Types
// ============================================

/**
 * Request for simulating workflow match
 */
export interface SimulateMatchRequest {
  jsonData: object;
  includeDetailedMatches?: boolean;
}

/**
 * Request for simulating workflow execution
 */
export interface SimulateExecutionRequest {
  orderData: object;
  totalAmount?: number;
  previousApproverIds?: string[];
}

/**
 * Request for full simulation (match + execution)
 */
export interface FullSimulationRequest {
  jsonData: object;
  totalAmount?: number;
  includeDetailedMatches?: boolean;
}

/**
 * Result of simulating which workflow would match
 */
export interface WorkflowSimulationResult {
  hasMatch: boolean;
  bestMatch?: WorkflowMatchResponseDto;
  alternativeMatches: WorkflowMatchResponseDto[];
  totalEvaluated: number;
  totalMatched: number;
  summary: string;
  simulatedAt: string;
}

/**
 * Workflow match response DTO
 */
export interface WorkflowMatchResponseDto {
  workflowId?: string;
  workflowName?: string;
  matched: boolean;
  matchScore: number;
  matchedIdentifiers: MatchedIdentifierDto[];
  reason?: string;
}

/**
 * Details about a matched identifier
 */
export interface MatchedIdentifierDto {
  matchIdentifierId: string;
  matchIdentifierName: string;
  type: string;
  matched: boolean;
  extractedValue?: any;
  extractedFrom?: string;
  reason?: string;
  weight: number;
  isRequired: boolean;
}

/**
 * Result of simulating workflow execution
 */
export interface ExecutionSimulationResult {
  workflow?: WorkflowSimulationInfo;
  success: boolean;
  errorMessage?: string;
  workflowSteps: SimulatedWorkflowStep[];
  estimatedDuration: string;
  estimatedCompletion?: string;
  summary: string;
  simulatedAt: string;
}

/**
 * Basic workflow info for simulation
 */
export interface WorkflowSimulationInfo {
  id: string;
  name: string;
  description?: string;
  type: string;
  active: boolean;
  totalLevels: number;
}

/**
 * Simulated workflow step with matched users
 */
export interface SimulatedWorkflowStep {
  step: number;
  name: string;
  description?: string;
  type: string;
  parallelApproval: boolean;
  requiredApprovals: number;
  timeoutHours?: number;
  amountThreshold?: number;
  matchedUsers: SimulatedMatchedUser[];
  wouldBeSkipped: boolean;
  skipReason?: string;
  warnings: string[];
}

/**
 * Matched user for simulation
 */
export interface SimulatedMatchedUser {
  userId: string;
  userName: string;
  email: string;
  role: string;
  matchedBy: string;
  isActive: boolean;
}

/**
 * Result of previewing approvers for a workflow
 */
export interface ApproverPreviewResult {
  workflow?: WorkflowSimulationInfo;
  success: boolean;
  errorMessage?: string;
  workflowSteps: SimulatedWorkflowStep[];
  totalUniqueApprovers: number;
  warnings: string[];
  generatedAt: string;
}

/**
 * Result of full simulation (match + execution)
 */
export interface FullSimulationResult {
  matchResult: WorkflowSimulationResult;
  executionResult?: ExecutionSimulationResult;
  success: boolean;
  summary: string;
  simulatedAt: string;
}

// ===========================================
// Workflow Recommendation Types
// ===========================================

/**
 * Request model for getting workflow recommendations
 */
export interface WorkflowRecommendationRequest {
  orderData: unknown;
  includeMatchingRecommendations?: boolean;
  includeApprovalRecommendations?: boolean;
  includeSkipConditionRecommendations?: boolean;
}

/**
 * Complete workflow recommendations based on order analysis
 */
export interface WorkflowRecommendationResult {
  success: boolean;
  errorMessage?: string;
  orderAnalysis: OrderAnalysis;
  matchingRecommendations: MatchingRecommendation[];
  approvalRecommendations: WorkflowStepRecommendations;
  skipConditionRecommendations: SkipConditionRecommendation[];
  warnings: string[];
  summary: string;
  generatedAt: string;
}

/**
 * Analysis of order data
 */
export interface OrderAnalysis {
  totalAmount?: number;
  currency?: string;
  lineItemCount: number;
  hasMultipleShipTos: boolean;
  hasInternationalShipping: boolean;
  detectedCountries: string[];
  discoveredFields: DiscoveredField[];
  priority?: string;
  sourceSystem?: string;
  hasHighValueItems: boolean;
  maxLineItemValue?: number;
  vendorName?: string;
  buyerInfo?: string;
  companyName?: string;
  department?: string;
  costCenter?: string;
}

/**
 * A field discovered in order JSON
 */
export interface DiscoveredField {
  fieldName: string;
  /** Position-based JSONPath (for backward compatibility) */
  jsonPath: string;
  /** Filter-based JSONPath expression (more robust, position-independent) */
  filterBasedJsonPath?: string;
  currentValue?: string;
  valueType: string;
  category: string;
  usefulnessScore: number;
  parentObject?: unknown;
  /** Whether this field was discovered from a name/value array pattern */
  isFromAttributePattern?: boolean;
  /** The name of the array containing this attribute (e.g., "Order_Attributes") */
  attributeArrayName?: string;
  /** The property name used for the attribute name (e.g., "Name", "name", "key") */
  attributeNameProperty?: string;
  /** The property name used for the attribute value (e.g., "Value", "value") */
  attributeValueProperty?: string;
}

/**
 * Recommendation for workflow matching criteria
 */
export interface MatchingRecommendation {
  fieldName: string;
  jsonPath: string;
  currentValue?: string;
  recommendedMatchType: string;
  suggestedIdentifier: SuggestedIdentifier;
  confidence: 'high' | 'medium' | 'low';
  reason: string;
  priority: number;
  category: string;
}

/**
 * Suggested identifier configuration
 */
export interface SuggestedIdentifier {
  name: string;
  description?: string;
  jsonPath: string;
  operator: string;
  value?: string;
  regexPattern?: string;
}

/**
 * Recommendations for workflow step configuration
 */
export interface WorkflowStepRecommendations {
  suggestedLevelCount: number;
  rationale: string;
  levels: WorkflowStepRecommendation[];
  suggestedThresholdTiers: ThresholdTier[];
}

/**
 * Recommendation for a single workflow step
 */
export interface WorkflowStepRecommendation {
  level: number;
  suggestedName: string;
  suggestedDescription?: string;
  suggestedApproverType: string;
  suggestedRole?: string;
  suggestedAmountThreshold?: number;
  suggestedSkipCondition?: string;
  suggestParallelApproval: boolean;
  suggestedTimeoutHours?: number;
  reason: string;
}

/**
 * Suggested threshold tier
 */
export interface ThresholdTier {
  minAmount: number;
  maxAmount?: number;
  label: string;
  suggestedLevels: number;
  suggestedRoles: string[];
}

/**
 * Recommendation for skip conditions
 */
export interface SkipConditionRecommendation {
  condition: string;
  expression: string;
  appliesTo: 'workflow' | 'level';
  levelNumber?: number;
  reason: string;
  riskLevel: 'low' | 'medium' | 'high';
  estimatedImpact?: string;
}

/**
 * Order audit report for printing
 */
export interface OrderAuditReport {
  orderId: string;
  orderNumber: string;
  status: string;
  createdAt: string;
  completedAt?: string;
  
  // Basic order information
  title: string;
  description: string;
  totalAmount: number;
  currency: string;
  submitterName: string;
  submitterEmail?: string;
  divisionName?: string;
  referenceNumber?: string;
  purchaseOrderNumber?: string;
  shippingMethod?: string;
  department?: string;
  costCenter?: string;
  
  // Order attributes (custom fields configured for the workflow), shown at the order level
  orderAttributes: OrderAttributeAudit[];
  
  // Workflow information
  workflowName: string;
  workflowId?: string;
  totalSteps: number;
  stepNames: Record<number, string>;
  
  // Approval workflow audit trail
  approvalSteps: ApprovalStepAudit[];
  
  // Line items
  lineItems: LineItemAudit[];
  
  // Addresses
  shippingAddress?: ShippingAddressDto;
  billingAddress?: BillingAddressDto;
  
  // Vendor information
  vendor?: VendorInfoDto;
  
  // Notes and comments
  notes?: string;
  
  // Processing time
  processingTime?: string; // TimeSpan as ISO 8601 duration string
}

/**
 * Approval step audit information
 */
export interface ApprovalStepAudit {
  level: number;
  stepName: string;
  status: string; // "Completed", "Pending", "InProgress", "Rejected"
  stepType: string; // "approval", "notification", "review", "fulfillment"
  startedAt?: string;
  completedAt?: string;
  processingTime?: string; // TimeSpan as ISO 8601 duration string
  
  // Approvers for this step
  approvers: ApproverAudit[];
}

/**
 * Individual approver audit information
 */
export interface ApproverAudit {
  approverName: string;
  approverEmail: string;
  decision: string; // "Approved", "Rejected", "Pending"
  comments?: string;
  actionDate?: string;
  responseTime?: string; // TimeSpan as ISO 8601 duration string
  signatureData?: string; // Base64 signature image (data URL) captured for Confirm Pickup steps
}

/**
 * Line item audit information
 */
export interface LineItemAudit {
  id: string;
  sku: string;
  name: string;
  requestedQuantity: number;
  approvedQuantity?: number;
  unitPrice: number;
  totalPrice: number;
  approvalStatus?: string;
  unitOfMeasure?: string;
  
  // Modification history for this line item
  modifications: LineItemModificationAudit[];
}

/**
 * Line item modification audit trail
 */
export interface LineItemModificationAudit {
  level: number;
  approverName: string;
  originalQuantity: number;
  modifiedQuantity: number;
  reason?: string;
  comments?: string;
  /** Raw decision for this line item at this step (e.g. "accept", "decline", "modify") */
  decision?: string;
  modifiedAt: string;
}

/**
 * An order attribute (custom order field) with its current value and change history.
 * Shown at the order level in the audit report.
 */
export interface OrderAttributeAudit {
  label: string;
  value?: string;
  changes: OrderAttributeChangeAudit[];
}

/**
 * A single change made to an order attribute during the approval process
 */
export interface OrderAttributeChangeAudit {
  step: number;
  modifiedBy: string;
  originalValue?: string;
  newValue: string;
  reason?: string;
  modifiedAt: string;
}

/**
 * Single activity event in the order timeline
 */
export interface OrderActivityEvent {
  /** ISO 8601 timestamp */
  occurredAt: string;
  /**
   * "OrderSubmitted" | "ApprovalDecision" | "ApproverAdded" | "EmailSent" | "StatusChanged" | "WorkflowPaused" | "WorkflowResumed"
   */
  eventType: string;
  /** Approval level this event belongs to; null for order-level events */
  level?: number;
  actorName?: string;
  actorEmail?: string;
  recipientName?: string;
  recipientEmail?: string;
  /** For ApprovalDecision: "Approved" / "Rejected" / "Timeout" / "Escalated" */
  decision?: string;
  comments?: string;
  details?: string;
  /** Hostname of the API instance that processed this event (null if not tracked for this event source) */
  processedByHost?: string;
}

/**
 * Full activity log for an order
 */
export interface OrderActivityLog {
  orderId: string;
  orderNumber: string;
  events: OrderActivityEvent[];
}

// Delegation types
export interface DelegationDto {
  id: string;
  delegatorUserId: string;
  delegatorName: string;
  delegatorEmail: string;
  delegateUserId: string;
  delegateName: string;
  delegateEmail: string;
  divisionId: string;
  divisionName: string;
  startDate: string;
  endDate: string;
  active: boolean;
  reason?: string;
  createdAt: string;
  updatedAt: string;
  isCurrentlyActive: boolean;
}

export interface CreateDelegationRequest {
  delegateUserId: string;
  divisionId: string;
  startDate: string;
  endDate: string;
  reason?: string;
}

export interface UpdateDelegationRequest {
  startDate?: string;
  endDate?: string;
  active?: boolean;
  reason?: string;
}

export interface DelegationListResponse {
  delegations: DelegationDto[];
  totalCount: number;
  pageSize: number;
  pageNumber: number;
}

export interface ActiveDelegationDto {
  delegationId: string;
  delegatorUserId: string;
  delegatorName: string;
  delegateUserId: string;
  delegateName: string;
  startDate: string;
  endDate: string;
  reason?: string;
}

// ─────────────────────── Workflow Templates ───────────────────────────────────

export interface RoutingRuleTemplateDto {
  id: string;
  field: string;   // e.g. "totalAmount", "submitterId", or "jsonPath"
  /** When field is "jsonPath", the actual JSONPath expression (e.g. "$.vendor.category"). Value is filled in later. */
  customPath?: string;
  operator: string; // equals | contains | greater-than | less-than | between
  label?: string;
}

export interface StepTemplateDto {
  step: number;
  name: string;
  description?: string;
  stepType: string; // approval | fulfillment | notification
  canEditQuantity: boolean;
  allowQuantityIncrease?: boolean; // When false (and canEditQuantity is true), approvers may only decrease quantities, never increase them
  parallelApproval: boolean;
  requiredApprovals: number;
  timeoutHours?: number;
  timeoutWarningHours?: number;
  orderLevelOnly: boolean;
  amountThreshold?: number;
  skipIfApprovedBy: string[];
  bypassIfNoApprover: boolean;
  auditBypass: boolean;
  requireExplicitSubmit?: boolean; // When true, line decisions at this step are staged as drafts until the reviewer clicks Submit Review
  /** Division role name that, when selected during public registration, assigns the registering user to this step. */
  registrationRoleName?: string;
}

export interface OrderAttributeTemplateDto {
  jsonPath: string;
  label: string;
  fieldType: string; // string | number | boolean | date
  description?: string;
  isRequired: boolean;
  isEditable: boolean;
  showValueInList: boolean;
  allowedValuesJsonPath?: string;
  minStep?: number;
  maxStep?: number;
  displayOrder: number;
}

export interface WorkflowTemplateDto {
  id: string;
  name: string;
  description?: string;
  type: string;
  isActive: boolean;
  routingRulesTemplate: RoutingRuleTemplateDto[];
  stepsTemplate: StepTemplateDto[];
  orderAttributesTemplate: OrderAttributeTemplateDto[];
  createdAt: string;
  updatedAt: string;
}

export interface CreateWorkflowTemplateRequest {
  name: string;
  description?: string;
  type: string;
  routingRulesTemplate: RoutingRuleTemplateDto[];
  stepsTemplate: StepTemplateDto[];
  orderAttributesTemplate: OrderAttributeTemplateDto[];
}

export interface UpdateWorkflowTemplateRequest {
  name: string;
  description?: string;
  type: string;
  isActive: boolean;
  routingRulesTemplate: RoutingRuleTemplateDto[];
  stepsTemplate: StepTemplateDto[];
  orderAttributesTemplate: OrderAttributeTemplateDto[];
}
